import type { Container, FillBand, FillEstimate, Pickup, SensorType } from "./api";
import { dayText } from "./accounts";

export const RESET_DAYS = 364;
export const RESET_REASON_MAX = 200;

const DAY_MS = 86_400_000;
const utc = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** The first day of the week of a date; Monday is the first day (rule A-4). */
export function weekStart(day: string): string {
  const ms = utc(day);
  const monday = (new Date(ms).getUTCDay() + 6) % 7;
  return iso(ms - monday * DAY_MS);
}

/** The newest pickup that the forecast counts after a reset on start (rule A-5). */
export function newestAfter(pickups: readonly Pickup[], start: string, today: string): string | null {
  const corrected = new Set(pickups.map((row) => row.corrects_id).filter(Boolean));
  const days = pickups.filter((row) => !corrected.has(row.id) && row.at >= start && row.at <= today).map((row) => row.at);
  if (start <= today) days.push(start);
  return days.length ? days.reduce((a, b) => (a > b ? a : b)) : null;
}

/** Weeks left as the projection counts them: the container weeks less the weeks since the newest pickup. */
export function weeksLeft(capWeeks: number | null, newest: string | null, today: string): number | null {
  if (capWeeks == null || newest == null) return null;
  const out = Math.round((utc(weekStart(today)) - utc(weekStart(newest))) / (7 * DAY_MS));
  return Math.floor(capWeeks - out);
}

/** "Due now", or the week when the next pickup is due. */
export function dueText(left: number | null, today: string): string {
  if (left == null) return "No estimate";
  if (left <= 0) return "Due now";
  return `Week of ${dayText(iso(utc(weekStart(today)) + left * 7 * DAY_MS), true)}`;
}

export function resetProblem(start: string, reason: string, today: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) return "Enter the start day.";
  if (start > today) return "The forecast cannot start after today.";
  if (start < iso(utc(today) - RESET_DAYS * DAY_MS)) return `The forecast must start in the last ${RESET_DAYS} days.`;
  return reasonProblem(reason);
}

export function reasonProblem(reason: string): string {
  const length = reason.trim().length;
  if (length > RESET_REASON_MAX) return `A reason is ${RESET_REASON_MAX} characters at most (now ${length}).`;
  return length < 3 ? "Give a reason of 3 characters or more." : "";
}

export function forecastFromText(from: string | null | undefined, reason: string | null | undefined): string {
  if (!from) return "";
  return reason ? `${dayText(from, true)} (${reason})` : dayText(from, true);
}

const pct = (share: number) => Math.round(share * 100);

/** The median fill, which also sets the dot colour, then the 10th to the 90th percentile: "About 62% full (55–70%)". */
export function bandText(band: Pick<FillBand, "p10" | "p50" | "p90">): string {
  const low = Math.min(pct(band.p10), 100);
  const mid = Math.min(pct(band.p50), 100);
  const high = pct(band.p90);
  if (low >= 100) return "Probably full";
  if (low === high) return `About ${mid}% full`;
  return `About ${mid}% full (${high > 100 ? `${low}% or more` : `${low}–${high}%`})`;
}

export function chanceText(risk: number): string {
  return risk < 0.01 ? "under 1% chance of overflow" : `${pct(risk)}% chance of overflow`;
}

export function sensorText(sensor: SensorType, floatPct: number): string {
  if (sensor === "float") return `Float at ${floatPct}%`;
  return sensor === "level" ? "Level sensor" : "No sensor";
}

/** The sensor of the containers as the nightly fit counts it: a level sensor, else the lowest float. */
export function containerSensor(containers: readonly Pick<Container, "sensor_type" | "float_pct">[]): { sensor: SensorType; floatPct: number } {
  if (containers.some((c) => c.sensor_type === "level")) return { sensor: "level", floatPct: 75 };
  const floats = containers.filter((c) => c.sensor_type === "float").map((c) => c.float_pct);
  return floats.length ? { sensor: "float", floatPct: Math.min(...floats) } : { sensor: "none", floatPct: 75 };
}

/** Why the estimate does not use the sensor that the containers have at this time (S-8), or "". */
export function sensorLagText(estimate: Pick<FillEstimate, "sensor" | "float_pct">, containers: readonly Pick<Container, "sensor_type" | "float_pct">[]): string {
  const now = containerSensor(containers);
  if (now.sensor === estimate.sensor && (now.sensor !== "float" || now.floatPct === estimate.float_pct)) return "";
  if (now.sensor === "none") return "No sensor now. The estimate changes at the nightly update.";
  return `${sensorText(now.sensor, now.floatPct)} now. The estimate uses it from the nightly update, if it reported in the last 2 days.`;
}

const ANCHOR_TEXT: Record<FillEstimate["anchor"]["type"], string> = {
  none: "",
  start: "Forecast start",
  empty: "Pickup",
  cross: "Float report",
  level: "Level report",
  driver: "Driver reading",
};

/** What the estimate starts from, as "Float report Oct 14". */
export function anchorText(anchor: FillEstimate["anchor"]): string {
  if (!anchor.at || anchor.type === "none") return "No pickup yet";
  return `${ANCHOR_TEXT[anchor.type]} ${dayText(anchor.at.slice(0, 10))}`;
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** A strong yearly swing, as "About 25% more in May". */
export function swingText(season: NonNullable<FillEstimate["season"]>): string {
  const day = new Date(Date.UTC(2026, 0, 1 + (season.peak_week - 1) * 7 + 3));
  return `About ${pct(season.amplitude)}% more in ${MONTHS[day.getUTCMonth()]}`;
}
